import { LocationType } from '../types';

/**
 * Surface area in tenths of a km², indexed by the taxonomy id the layer joins
 * on; 0 marks an id the tileset has no polygon for. A typed array indexed by
 * id costs 4 bytes per id and stays off V8's dictionary path — the same
 * table as a string-keyed object is ~4× the heap and ~2× the bundle.
 */
export type SurfaceAreasById = Uint32Array;

/** Surface area in km² keyed by a code or name (ISO country code, region). */
type SurfaceAreasByKey = Readonly<Record<string, number>>;

export interface SurfaceAreaTables {
  [LocationType.REGION]: SurfaceAreasByKey;
  [LocationType.COUNTRY]: SurfaceAreasByKey;
  [LocationType.METRO_AREA]: SurfaceAreasById;
  [LocationType.STATE]: SurfaceAreasById;
  [LocationType.COUNTY]: SurfaceAreasById;
  [LocationType.CITY]: SurfaceAreasById;
}

export const SurfaceAreas: SurfaceAreaTables = {
  [LocationType.REGION]: {
    antarctic: 12397617,
    'arab states': 3516380,
    'central america and the caribbean': 2701425,
    'central and western asia': 5250999,
    'eastern asia': 11558035,
    'eastern europe': 18721142,
    'north america': 21542573,
    'northern africa': 7623590,
    'northern europe': 1792084,
    'pacific islands': 8496789,
    'south america': 183325,
    'south-eastern asia': 4418853,
    'southern america': 17508055,
    'southern asia': 6738359,
    'southern europe': 1309411,
    'sub-saharan africa': 22283427,
    'western europe': 1100416,
  },
  [LocationType.COUNTRY]: {},
  [LocationType.METRO_AREA]: new Uint32Array(0),
  [LocationType.STATE]: new Uint32Array(0),
  [LocationType.COUNTY]: new Uint32Array(0),
  [LocationType.CITY]: new Uint32Array(0),
};

/** km² for an id, or null when the tileset carries no polygon for it. */
export function getSurfaceAreaById(
  table: SurfaceAreasById,
  id: string | number
): number | null {
  const tenths = table[Number(id)];
  return tenths ? tenths / 10 : null;
}

/** The ids a table carries an area for — exactly the tileset's joinable ids. */
export function surfaceAreaIds(table: SurfaceAreasById): string[] {
  const ids: string[] = [];
  for (let id = 0; id < table.length; id++) {
    if (table[id]) ids.push(String(id));
  }
  return ids;
}

let generatedAreasPromise: Promise<void> | null = null;

export function loadGeneratedSurfaceAreas(): Promise<void> {
  generatedAreasPromise ??= Promise.all([
    import('./generated/country-surface-areas'),
    import('./generated/metro-surface-areas'),
    import('./generated/state-surface-areas'),
    import('./generated/county-surface-areas'),
    import('./generated/city-surface-areas'),
  ])
    .then(([country, metro, state, county, city]) => {
      SurfaceAreas[LocationType.COUNTRY] = country.COUNTRY_SURFACE_AREAS;
      SurfaceAreas[LocationType.METRO_AREA] = metro.METRO_SURFACE_AREAS;
      SurfaceAreas[LocationType.STATE] = state.STATE_SURFACE_AREAS;
      SurfaceAreas[LocationType.COUNTY] = county.COUNTY_SURFACE_AREAS;
      SurfaceAreas[LocationType.CITY] = city.CITY_SURFACE_AREAS;
    })
    .catch((error) => {
      // Don't cache a failed chunk load: let the next caller retry.
      generatedAreasPromise = null;
      throw error;
    });
  return generatedAreasPromise;
}
